/**
 * analytics.ts — pure helpers for the Flight Deck departure board (nav 04).
 *
 * Wraps the Distribution page's analytics (the dataset shape, member type, and
 * filter helpers) so the Flight Deck and the Distribution page can never
 * disagree on a number. Adds the campaign-lifecycle model, the headline-KPI
 * resolver, the reduced slicer set, as-of-date recompute, and portfolio
 * aggregation. Every function is pure.
 */
import type { Campaign } from '@pages/setup/types';
import { type DistMember, type SlicerDef, type Filters, INDICATORS } from '../distribution/analytics';

export type { DistMember, Filters };

// ─── dates ───────────────────────────────────────────────────────────────────

/** Parse a 'YYYY-MM-DD' string to a local date-only Date. */
export function parseISO(s: string): Date {
  const [y, m, d] = (s || '').split('-').map((n) => Number(n));
  return new Date(y || 1970, (m || 1) - 1, d || 1);
}

/** Whole days from a to b (b - a). */
export function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

/** Today as a local date-only Date. */
export function todayDate(): Date {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

export const daysToLaunch = (c: Campaign, today: Date): number =>
  daysBetween(today, parseISO(c.launch));

export const campaignDurationDays = (c: Campaign): number =>
  Math.max(0, daysBetween(parseISO(c.launch), parseISO(c.endDate)));

// ─── lifecycle ───────────────────────────────────────────────────────────────

export type Lifecycle = 'inactive' | 'upcoming' | 'active' | 'final-call' | 'closed';

/** Derived campaign lifecycle — drives every card's appearance (spec §5). */
export function lifecycleOf(c: Campaign, today: Date): Lifecycle {
  // Board-only campaigns are historical Flight Deck references: keep their card
  // on its real (date-derived) lifecycle with final numbers, never 'inactive',
  // regardless of the `active` flag. This hardwires the prior-campaign cards so
  // deactivating them (to drop the "Active in header" count) can't blank the
  // board (William 2026-06-17). Non-board-only campaigns keep the legacy rule.
  if (!c.active && !c.boardOnly) return 'inactive';
  if (c.archived) return 'closed';
  const launch = parseISO(c.launch);
  const end = parseISO(c.endDate);
  if (today < launch) return 'upcoming';
  if (today > end) return 'closed';
  if (daysBetween(today, end) <= 14) return 'final-call';
  return 'active';
}

export type BoardTone = 'teal' | 'amber' | 'muted' | 'gray';

export const LIFECYCLE_META: Record<Lifecycle, { chip: string; tone: BoardTone }> = {
  upcoming: { chip: 'BOARDING', tone: 'teal' },
  active: { chip: 'IN FLIGHT', tone: 'teal' },
  'final-call': { chip: 'FINAL CALL', tone: 'amber' },
  closed: { chip: 'ARRIVED', tone: 'muted' },
  inactive: { chip: 'CANCELLED', tone: 'gray' },
};

/**
 * The per-program route slug (e.g. 'fobt'), distinct from the campaign id
 * ('fobt-2026-q1'). Derived by stripping a trailing -YYYY or -YYYY-qN.
 * Made explicit here per spec §23-C.
 */
export function routeSlugOf(c: Campaign): string {
  return c.id.replace(/-\d{4}(-q\d+)?$/i, '') || c.id;
}

/** Pre-launch readiness ticks for an upcoming card (spec §8.4). */
export function launchReadiness(c: Campaign): { approved: boolean; materials: boolean; waves: boolean } {
  const m = c.materials;
  const materials = !!m && (
    (m.videoScreens?.length ?? 0) > 0 ||
    (m.audioCards?.length ?? 0) > 0 ||
    (m.quickSurvey?.length ?? 0) > 0
  );
  return {
    approved: !!c.approvedOn,
    materials,
    waves: (c.waves?.length ?? 0) > 0,
  };
}

// ─── slicers (reduced set — spec §14.1) ──────────────────────────────────────

export const FD_SLICERS: SlicerDef[] = [
  { key: 'state', label: 'State', group: 'Geography', accessor: (m) => m.state },
  { key: 'market', label: 'Market', group: 'Geography', accessor: (m) => m.market },
  { key: 'county', label: 'County', group: 'Geography', accessor: (m) => m.county, searchable: true },
  { key: 'gender', label: 'Gender', group: 'Demographics', accessor: (m) => m.gender },
  { key: 'ageBand', label: 'Age band', group: 'Demographics', accessor: (m) => m.ageBand },
  { key: 'language', label: 'Language', group: 'Demographics', accessor: (m) => m.language, searchable: true },
  { key: 'cohort', label: 'Cohort', group: 'Program', accessor: (m) => m.cohort },
];

export const FD_SLICER_GROUPS = ['Geography', 'Demographics', 'Program'];

/** AND across slicers, OR within a slicer. Member-level dimensional filter. */
export function fdApplyFilters(members: DistMember[], filters: Filters): DistMember[] {
  const active = FD_SLICERS.filter((s) => (filters[s.key]?.length ?? 0) > 0);
  if (active.length === 0) return members;
  return members.filter((m) => active.every((s) => filters[s.key]!.includes(s.accessor!(m))));
}

/** Count of engaged slicers, including the as-of-date control. */
export function fdActiveCount(filters: Filters, asOf: string | null): number {
  const dims = FD_SLICERS.reduce((n, s) => n + ((filters[s.key]?.length ?? 0) > 0 ? 1 : 0), 0);
  return dims + (asOf ? 1 : 0);
}

// ─── as-of-date stage recompute (spec §14.3) ─────────────────────────────────

/**
 * A member's furthest kit stage as of a date (ISO day string), or its current
 * stage when asOf is null. Stage indices: 1 ordered, 2 prepared, 3 shipped,
 * 4 delivered, 5 returned, 6 resulted. ISO day strings compare chronologically.
 */
export function stageAsOf(m: DistMember, asOf: string | null): number {
  if (!asOf) return m.kitStageIndex;
  let s = 0;
  if (m.orderPlacedAt && m.orderPlacedAt <= asOf) s = 1;
  if (m.kitPreparedAt && m.kitPreparedAt <= asOf) s = Math.max(s, 2);
  if (m.kitShippedAt && m.kitShippedAt <= asOf) s = Math.max(s, 3);
  if (m.kitDeliveredAt && m.kitDeliveredAt <= asOf) s = Math.max(s, 4);
  if (m.sampleReceivedAt && m.sampleReceivedAt <= asOf) s = Math.max(s, 5);
  if (m.sampleResultedAt && m.sampleResultedAt <= asOf) s = Math.max(s, 6);
  return s;
}

// ─── compact kit-lifecycle funnel (spec §13) ─────────────────────────────────

export const FUNNEL_STAGES: Array<{ label: string; stage: number }> = [
  { label: 'Ordered', stage: 1 },
  { label: 'Shipped', stage: 3 },
  { label: 'Delivered', stage: 4 },
  { label: 'Returned', stage: 5 },
  { label: 'Resulted', stage: 6 },
];

export interface FunnelBar {
  label: string;
  value: number;
  pct: number; // percent of the Ordered stage
}

/** Five-stage compact funnel, as-of-aware. */
export function fdFunnel(members: DistMember[], asOf: string | null): FunnelBar[] {
  const stages = members.map((m) => stageAsOf(m, asOf));
  const top = stages.filter((s) => s >= 1).length || 1;
  return FUNNEL_STAGES.map((fs) => {
    const value = stages.filter((s) => s >= fs.stage).length;
    return { label: fs.label, value, pct: Math.round((value / top) * 1000) / 10 };
  });
}

// ─── headline KPI resolver (spec §8.3) ───────────────────────────────────────

export interface Headline {
  /** Display label, e.g. 'KIT RETURN RATE'. */
  label: string;
  /** Formatted value for the split-flap, e.g. '27.4%' or '--'. */
  display: string;
  /** Numeric value (percent), or null when not computable. */
  value: number | null;
  formula: string;
  about: string;
}

const isDeviceRetained = (c: Campaign): boolean =>
  /no return|retain/i.test(c.kit?.returnMethod ?? '');

/**
 * Resolve the headline metric for an active/closed campaign. Return rate for a
 * mailed-kit program; a participation metric for a device-retained program
 * (BP Cuffs) where a return rate is structurally undefined.
 */
export function headlineMetric(c: Campaign, members: DistMember[] | null, asOf: string | null): Headline {
  if (isDeviceRetained(c)) {
    const formula = 'Members with ≥1 submitted reading ÷ Cohort × 100';
    const about =
      'A device-retained program mails no kit back, so a return rate is undefined. Participation stands in: the share of the cohort that has submitted at least one reading.';
    if (!members || members.length === 0) {
      return { label: 'READINGS SUBMITTED', display: '--', value: null, formula, about };
    }
    const submitted = members.filter((m) => stageAsOf(m, asOf) >= 1).length;
    const v = (submitted / members.length) * 100;
    return { label: 'READINGS SUBMITTED', display: `${v.toFixed(1)}%`, value: v, formula, about };
  }

  const ind = INDICATORS.find((i) => i.key === 'returnRate');
  const formula = ind?.formula ?? 'Samples returned ÷ Kits delivered × 100';
  const about = ind?.about ?? 'Delivered kits the member mails back — the headline FIT-program metric.';
  if (!members || members.length === 0) {
    return { label: 'KIT RETURN RATE', display: '--', value: null, formula, about };
  }
  const delivered = members.filter((m) => stageAsOf(m, asOf) >= 4).length;
  const returned = members.filter((m) => stageAsOf(m, asOf) >= 5).length;
  const v = delivered > 0 ? (returned / delivered) * 100 : null;
  return {
    label: 'KIT RETURN RATE',
    display: v === null ? '--' : `${v.toFixed(1)}%`,
    value: v,
    formula,
    about,
  };
}

// ─── portfolio aggregation (spec §7.2) ───────────────────────────────────────

export interface CampaignSlice {
  campaign: Campaign;
  lifecycle: Lifecycle;
  /** Cross-filtered member set, or null when the campaign has no perf data. */
  members: DistMember[] | null;
  /** Unfiltered member set — feeds the slicer rail's option universe. */
  allMembers?: DistMember[] | null;
  /** Dataset era, for the as-of-date bounds. */
  era?: { start: string; end: string } | null;
}

export interface PortfolioSummary {
  inFlight: number;
  membersInMotion: number;
  kitsReturned: number;
  avgReturnRate: number | null;
  nextLaunch: { name: string; days: number } | null;
}

/** Aggregate the visible cards into the portfolio-summary strip figures. */
export function portfolioSummary(
  slices: CampaignSlice[],
  today: Date,
  asOf: string | null,
): PortfolioSummary {
  let inFlight = 0;
  let membersInMotion = 0;
  let kitsReturned = 0;
  let pooledReturned = 0;
  let pooledDelivered = 0;
  let nextLaunch: { name: string; days: number } | null = null;

  for (const s of slices) {
    if (s.lifecycle === 'inactive') continue;
    if (s.lifecycle === 'active' || s.lifecycle === 'final-call') inFlight += 1;
    if (s.members) {
      membersInMotion += s.members.length;
      const returned = s.members.filter((m) => stageAsOf(m, asOf) >= 5).length;
      const delivered = s.members.filter((m) => stageAsOf(m, asOf) >= 4).length;
      kitsReturned += returned;
      pooledReturned += returned;
      pooledDelivered += delivered;
    }
    if (s.lifecycle === 'upcoming') {
      const d = daysToLaunch(s.campaign, today);
      if (nextLaunch === null || d < nextLaunch.days) {
        nextLaunch = { name: s.campaign.nickname || s.campaign.code, days: d };
      }
    }
  }

  return {
    inFlight,
    membersInMotion,
    kitsReturned,
    avgReturnRate: pooledDelivered > 0 ? (pooledReturned / pooledDelivered) * 100 : null,
    nextLaunch,
  };
}

// ─── misc display helpers ────────────────────────────────────────────────────

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'Apr 15, 2026' from a 'YYYY-MM-DD' string. */
export function fmtDate(iso: string): string {
  if (!iso) return '—';
  const d = parseISO(iso);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** 'Apr 15' (no year) from a 'YYYY-MM-DD' string. */
export function fmtDateShort(iso: string): string {
  if (!iso) return '—';
  const d = parseISO(iso);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}
